import { z } from 'zod';

const boundedPath = z.string().min(1).max(4096).refine(
  (value) => !/[\x00-\x1f\x7f]/.test(value), 'Paths cannot contain control characters',
);
const objectKey = z.string().min(1).max(1024).refine(
  (value) => !value.startsWith('/') && !/[\\\x00-\x1f\x7f]/.test(value) &&
    value.split('/').every((part) => part !== '' && part !== '.' && part !== '..'),
  'Use a relative object key without empty segments or traversal',
);
const remoteStorage = z.object({
  backend: z.literal('remote'),
  bucket: z.string().min(3).max(63).regex(/^[a-z0-9][a-z0-9.-]*[a-z0-9]$/)
    .refine((value) => !value.includes('..') && !/^\d+\.\d+\.\d+\.\d+$/.test(value), 'Invalid bucket name'),
  region: z.string().max(64).regex(/^[a-z]{2}(?:-[a-z]+)+-\d+$/, 'Use an AWS region such as us-east-1'),
  prefix: z.string().min(2).max(512).refine((value) => value.endsWith('/') &&
    objectKey.safeParse(value.slice(0, -1)).success, 'Use a relative prefix ending in /'),
}).strict();

/** Dashboard-owned settings. Operation IDs, deadlines and credentials are not configuration. */
export const gameSaveCampaignConfigurationSchema = z.discriminatedUnion('enabled', [
  z.object({ enabled: z.literal(false) }).strict(),
  z.object({
    enabled: z.literal(true),
    savePaths: z.array(boundedPath).min(1).max(128),
    allowedRoots: z.array(boundedPath).min(1).max(32).optional(),
    requireStopped: z.boolean(),
    storage: z.discriminatedUnion('backend', [z.object({ backend: z.literal('local') }).strict(), remoteStorage]),
    baseline: z.object({ objectKey, sha256: z.string().regex(/^[a-f0-9]{64}$/, 'Use the archive SHA-256 in lowercase hexadecimal') }).strict().optional(),
  }).strict(),
]);

export type GameSaveCampaignConfiguration = z.infer<typeof gameSaveCampaignConfigurationSchema>;

function windowsPath(value: string): string | undefined {
  const expanded = value.replaceAll('%USERPROFILE%', 'C:\\GameAccount')
    .replaceAll('%LOCALAPPDATA%', 'C:\\GameAccount\\AppData\\Local')
    .replaceAll('%APPDATA%', 'C:\\GameAccount\\AppData\\Roaming').replaceAll('/', '\\');
  if (!/^[A-Za-z]:\\.+/.test(expanded) || expanded.includes('%')) return undefined;
  const parts = expanded.slice(3).split('\\').filter(Boolean);
  if (parts.some((part) => part === '.' || part === '..' || part.length > 255 || /[. ]$/.test(part) ||
    /[<>:"|?*\x00-\x1f\x7f]/.test(part) || /^(?:CON|PRN|AUX|NUL|CONIN\$|CONOUT\$|(?:COM|LPT)[1-9¹²³])(?:\.|$)/i.test(part))) return undefined;
  return expanded.replace(/\\+$/, '').toLowerCase();
}

function inside(root: string, candidate: string, separator: string): boolean {
  return candidate === root || candidate.startsWith(root + separator);
}

/** Cross-field checks shared by the campaign API and dashboard before saving. */
export function validateGameSaveCampaignConfiguration(
  // Zod infers object fields as optional when a consumer disables strictNullChecks.
  campaign: { launchPackage?: { launcher?: string; launchId?: string; package?: string }; gameSave?: GameSaveCampaignConfiguration }, ctx: z.RefinementCtx,
): void {
  const configuration = campaign.gameSave;
  if (!configuration?.enabled) return;
  const issue = (field: string, message: string, index?: number) => ctx.addIssue({
    code: z.ZodIssueCode.custom, path: ['gameSave', field, ...(index === undefined ? [] : [index])], message,
  });
  let paths: string[] = [];
  const launcher = campaign.launchPackage ?? {};
  if (launcher.launcher === 'steam') {
    if (!/^[1-9][0-9]{0,9}$/.test(launcher.launchId ?? '') || Number(launcher.launchId) > 2147483647)
      issue('savePaths', 'Steam App ID must be a positive native App ID');
    if (!configuration.allowedRoots?.length) issue('allowedRoots', 'Steam saves require allowed Windows roots');
    const roots = (configuration.allowedRoots ?? []).map((root, index) => {
      const normalized = windowsPath(root);
      if (!normalized) issue('allowedRoots', 'Use an absolute local Windows path or a supported profile template', index);
      return normalized;
    });
    paths = configuration.savePaths.map((path, index) => {
      const normalized = windowsPath(path);
      if (!normalized) issue('savePaths', 'Use an absolute local Windows path or a supported profile template', index);
      else if (!roots.some((root) => root && inside(root, normalized, '\\')))
        issue('savePaths', 'Save path must be inside an allowed Windows root', index);
      return normalized ?? path;
    });
  } else if (launcher.launcher === 'android') {
    if (!/^[A-Za-z][A-Za-z0-9_]*(\.[A-Za-z][A-Za-z0-9_]*)+$/.test(launcher.package ?? '') || (launcher.package?.length ?? 0) > 256)
      issue('savePaths', 'Android package must be a valid package identifier');
    if (configuration.allowedRoots) issue('allowedRoots', 'Windows allowed roots do not apply to Android');
    const roots = ['/sdcard/Android/data/', '/storage/emulated/0/Android/data/', '/data/data/', '/data/user/0/']
      .map((root) => root + launcher.package);
    paths = configuration.savePaths.map((path, index) => {
      if (!path.startsWith('/') || path.endsWith('/') || path.includes('//') || path.includes('\\') ||
        path.split('/').some((part) => part === '.' || part === '..') || !roots.some((root) => inside(root, path, '/')))
        issue('savePaths', 'Use a data path inside this Android package', index);
      return path.replace(/^\/sdcard\//, '/storage/emulated/0/').replace(/^\/data\/data\//, '/data/user/0/');
    });
  } else issue('savePaths', 'Game saves support Steam and Android launchers');
  paths.forEach((path, index) => {
    if (paths.slice(0, index).some((previous) => inside(previous, path, launcher.launcher === 'steam' ? '\\' : '/') ||
      inside(path, previous, launcher.launcher === 'steam' ? '\\' : '/')))
      issue('savePaths', 'Save paths cannot overlap or reference the same directory', index);
  });
  if (configuration.storage.backend === 'remote' && configuration.baseline &&
    !configuration.baseline.objectKey.startsWith(configuration.storage.prefix))
    issue('baseline', 'Baseline object key must be inside the configured remote prefix');
}

/** Validate the newly added settings without stripping or checking unrelated legacy campaign fields. */
export const gameSaveCampaignSettingsSchema = z.object({
  launchPackage: z.object({ launcher: z.string(), launchId: z.string().optional(), package: z.string().optional() }).passthrough(),
  gameSave: gameSaveCampaignConfigurationSchema.optional(),
}).superRefine(validateGameSaveCampaignConfiguration);
